import { useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Button,
  DataGrid,
  DatePicker,
  Input,
  messageFromError,
  SearchableSelect,
  monthOnly,
  MonthStepper,
  PageFrame,
  shiftMonth,
  toast,
  unwrapAction,
  useGridConfig,
  searchableSource,
} from '@eo/shared';

import type { ClientItem, HolidayItem, TimeOffItem } from './api/types';
import { INTEGRATION_PROVIDERS, SPLIT_CATEGORIES } from './constants';
import { makeClientsGridConfig } from './config/clientsGridConfig';
import { makeHolidaysGridConfig, makeTimeOffGridConfig } from './config/gridConfig';
import { assignableClientOptions, capacitySyncLabel, ownerNames } from './helpers';
import type { UserDetailContentProps } from './types';

export function UserDetailContent({
  data,
  onUpsertSplit,
  onCreateTimeOff,
  onRemoveTimeOff,
  onSetClientOwner,
  onQueryOptions,
}: UserDetailContentProps) {
  const [, setSearchParams] = useSearchParams();
  const [splitDraft, setSplitDraft] = useState<Record<string, string> | null>(null);
  const [pto, setPto] = useState({ date: '', hours: '8', notes: '' });
  const [clientToAdd, setClientToAdd] = useState('');
  const [busy, setBusy] = useState(false);

  const capacity = data.capacity;
  const month = data.month;

  const onMonthShift = (delta: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('month', shiftMonth(month, delta));
      return params;
    });
  };

  const run = async (work: () => Promise<void>, failure: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await work();
    } catch (err) {
      toast.error(messageFromError(err, failure));
    } finally {
      setBusy(false);
    }
  };

  const addableClients = useMemo(
    () =>
      capacity
        ? assignableClientOptions(
            data.options.clients.items,
            capacity.user.id,
            ownerNames(data.staff),
          )
        : [],
    [data.options.clients.items, data.staff, capacity],
  );
  const clientSearch = searchableSource(data.options.clients, (q) =>
    onQueryOptions({ source: 'clients', ...q }),
  );

  const clientsConfig = useGridConfig(makeClientsGridConfig, {
    onRemove: (row: ClientItem) =>
      void run(async () => {
        unwrapAction(
          await onSetClientOwner({
            clientId: row.id,
            ownerUserId: null,
            memberId: row.ownerUserId ?? '',
          }),
        );
        toast.success(`${row.name} unassigned.`);
      }, 'Unassigning the client failed. Try again.'),
  });

  const timeOffConfig = useGridConfig(makeTimeOffGridConfig, {
    onRemove: (row: TimeOffItem) =>
      void run(async () => {
        unwrapAction(await onRemoveTimeOff({ id: row.id, userId: capacity!.user.id, month }));
        toast.success('Time off removed.');
      }, 'Removing time off failed. Try again.'),
  });
  const holidaysConfig = useGridConfig(makeHolidaysGridConfig, undefined);

  if (!capacity) return <p className="p-6 text-sm text-text-tertiary">No member loaded.</p>;

  const { user, integrations } = capacity;

  const draft =
    splitDraft ??
    Object.fromEntries(
      SPLIT_CATEGORIES.map(({ key }) => [
        key,
        String(capacity.split?.allocations[key] ?? (key === 'csd' ? 100 : 0)),
      ]),
    );
  const sum = SPLIT_CATEGORIES.reduce((total, { key }) => total + Number(draft[key] || 0), 0);

  return (
    <div className="flex flex-col">
      <PageFrame
        backLink={
          <Link
            to="/admin?tab=registries&view=staff"
            className="flex w-fit items-center gap-1 text-xs text-text-tertiary hover:text-text-primary"
          >
            <ArrowLeft className="size-3" />
            Staff
          </Link>
        }
        title={user.name}
        subtitle={`Everything configured about this member for ${monthOnly(month)}.${
          user.isActive ? '' : ' This member is inactive.'
        }`}
        controls={<MonthStepper month={month} onShift={onMonthShift} />}
      />

      <div className="flex flex-col gap-6 px-[var(--page-margin)] pb-8">
        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-text-primary">Integrations</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {INTEGRATION_PROVIDERS.map((provider) => (
              <div
                key={provider}
                className="rounded-16 border border-border-faint bg-[var(--color-canvas-base)] p-3"
              >
                <p className="text-xs capitalize text-text-tertiary">{provider}</p>
                <p className="text-sm font-medium text-text-primary">
                  {integrations[provider].connected
                    ? integrations[provider].preview
                    : 'Not connected'}
                </p>
              </div>
            ))}
          </div>
          <p data-testid="capacity-sync" className="text-xs text-text-tertiary">
            {capacitySyncLabel(capacity.capacitySync, integrations.clickup.connected)}
          </p>
        </section>

        <section className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-sm font-semibold text-text-primary">Capacity split</h2>
              <p className="text-xs text-text-tertiary">
                Percent of {monthOnly(month)} capacity per work category — must sum to 100.
              </p>
              {!capacity.split && (
                <p className="text-xs text-destructive">
                  No split for this month, so Capacity counts every hour as client work.
                </p>
              )}
            </div>
            <Button
              disabled={busy || sum !== 100}
              onClick={() =>
                void run(async () => {
                  unwrapAction(
                    await onUpsertSplit({
                      userId: user.id,
                      month,
                      allocations: Object.fromEntries(
                        SPLIT_CATEGORIES.map(({ key }) => [key, Number(draft[key])]),
                      ),
                    }),
                  );
                  setSplitDraft(null);
                  toast.success('Split saved.');
                }, 'Saving the split failed. Try again.')
              }
            >
              Save split
            </Button>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            {SPLIT_CATEGORIES.map(({ key, label }) => (
              <label key={key} className="flex flex-col gap-1 text-xs text-text-tertiary">
                {label} %
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={draft[key]}
                  onChange={(e) => setSplitDraft({ ...draft, [key]: e.target.value })}
                  aria-label={`${label} split`}
                  className="w-24"
                />
              </label>
            ))}
            <span
              className={sum !== 100 ? 'text-sm text-destructive' : 'text-sm text-text-tertiary'}
            >
              Sum: {sum}%
            </span>
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="text-sm font-semibold text-text-primary">Clients</h2>
            <p className="text-xs text-text-tertiary">
              The clients this member owns. A client has one owner, so adding one that belongs to
              someone else moves it here.
            </p>
          </div>
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!clientToAdd) return;
              void run(async () => {
                unwrapAction(
                  await onSetClientOwner({
                    clientId: clientToAdd,
                    ownerUserId: user.id,
                    memberId: user.id,
                  }),
                );
                setClientToAdd('');
                toast.success('Client assigned.');
              }, 'Assigning the client failed. Try again.');
            }}
          >
            <div className="w-72">
              <SearchableSelect
                value={clientToAdd}
                ariaLabel="Client to assign"
                placeholder="Select a client"
                emptyLabel="No client matches"
                selectedLabel={addableClients.find((o) => o.value === clientToAdd)?.label}
                options={addableClients}
                {...clientSearch}
                onChange={setClientToAdd}
              />
            </div>
            <Button type="submit" disabled={busy || !clientToAdd}>
              Add client
            </Button>
          </form>
          <DataGrid<ClientItem>
            config={clientsConfig}
            rows={data.clients}
            pagination={data.clientsPagination}
          />
        </section>

        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-text-primary">Time off (this month)</h2>
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!pto.date) return;
              void run(async () => {
                unwrapAction(
                  await onCreateTimeOff({
                    userId: user.id,
                    date: pto.date,
                    hours: Number(pto.hours) || 8,
                    notes: pto.notes || undefined,
                    month,
                  }),
                );
                setPto({ date: '', hours: '8', notes: '' });
                toast.success('Time off recorded.');
              }, 'Recording time off failed. Try again.');
            }}
          >
            <DatePicker
              value={pto.date}
              onChange={(value) => setPto((p) => ({ ...p, date: value }))}
              ariaLabel="Time off date"
              className="w-40"
            />
            <Input
              type="number"
              min={0.5}
              max={8}
              step={0.5}
              value={pto.hours}
              onChange={(e) => setPto((p) => ({ ...p, hours: e.target.value }))}
              aria-label="Time off hours"
              className="w-24"
            />
            <Input
              value={pto.notes}
              onChange={(e) => setPto((p) => ({ ...p, notes: e.target.value }))}
              placeholder="Notes"
              aria-label="Time off notes"
              className="w-48"
            />
            <Button type="submit" disabled={busy || !pto.date}>
              Add time off
            </Button>
          </form>
          <DataGrid<TimeOffItem> config={timeOffConfig} rows={capacity.timeOff} />
        </section>

        <section className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="text-sm font-semibold text-text-primary">
              Holidays{user.country ? ` (${user.country})` : ''}
            </h2>
            <p className="text-xs text-text-tertiary">
              Read-only reference. Holidays are configured per country on Admin&apos;s Availability
              panel.
            </p>
          </div>
          <DataGrid<HolidayItem> config={holidaysConfig} rows={capacity.holidays} />
        </section>
      </div>
    </div>
  );
}
